// src/pages/auth/ForgotPassword.jsx
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Link,
  Alert,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Import custom components and hooks
import { TextField, Button } from '../../design-system';
import useAuthStore from '../../stores/authStore';
import useNotificationStore from '../../stores/notificationStore';
import useUIStore from '../../stores/uiStore';

// Forgot password page component
const ForgotPassword = () => {
  const navigate = useNavigate();
  const { forgotPassword, isLoading, error, clearError } = useAuthStore();
  const { showSuccess } = useNotificationStore();
  const { addNotification } = useUIStore();
  
  // Success state to show success message
  const [isSuccess, setIsSuccess] = useState(false);
  // For redirect countdown
  const [countdown, setCountdown] = useState(5);
  // For progress indicator
  const [progress, setProgress] = useState(0);
  
  // Clear any auth errors when component unmounts
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);
  
  // Handle redirect countdown after success
  useEffect(() => {
    let timer;
    let progressTimer;
    
    if (isSuccess) {
      // Start countdown
      timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/login');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Update progress bar
      progressTimer = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 5;
          return newProgress >= 100 ? 100 : newProgress;
        });
      }, 250);
    }
    
    return () => {
      clearInterval(timer);
      clearInterval(progressTimer);
    };
  }, [isSuccess, navigate]);
  
  // Validation schema using Yup
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Email is required'),
  });
  
  // Formik setup
  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const result = await forgotPassword(values.email);
        setIsSuccess(true);
        
        // Show notification using both systems for better visibility
        showSuccess('Password reset instructions have been sent to your email.');
        addNotification({
          type: 'success',
          title: 'Email Sent',
          message: 'Password reset instructions have been sent to your email.',
          duration: 8000
        });
        // Don't reset form - keep the email visible
      } catch (err) {
        // Error will be handled by the auth store and displayed in the error alert
      }
    },
  });
  
  return (
    <Box sx={{ width: '100%' }}>
      <Typography component="h1" variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
        Forgot Password
      </Typography>
      
      {!isSuccess ? (
        <>
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', mb: 3 }}>
            Enter your email address and we'll send you instructions to reset your password.
          </Typography>
          
          {/* Error alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {/* Forgot password form */}
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email Address"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              magical
              margin="normal"
              autoComplete="email"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              
              size="large"
              magical
              loading={isLoading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              Send Reset Instructions
            </Button>
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to="/login"
                variant="body2"
                underline="hover"
                sx={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: (theme) => theme.palette.mode === 'dark' ? 'secondary.main' : 'primary.main',
                  fontWeight: 'medium',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: (theme) => theme.palette.mode === 'dark' ? 'secondary.light' : 'primary.dark',
                    transform: 'translateY(-1px)',
                  }
                }}
              >
                <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} />
                Back to Sign In
              </Link>
            </Box>
          </form>
        </>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Reset instructions sent! Please check your email.
          </Alert>
          
          <Typography variant="body2" paragraph>
            We've sent an email to <strong>{formik.values.email}</strong> with instructions to reset your password.
            The link in the email will expire in 30 minutes.
          </Typography>
          
          <Typography variant="body2" paragraph>
            If you don't see the email in your inbox, please check your spam folder.
          </Typography>
          
          {/* Countdown indicator */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 1 }}>
              Redirecting to login in {countdown} seconds...
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{
                height: 6,
                borderRadius: 3,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                }
              }}
            />
          </Box>
          
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              color={(theme) => theme.palette.mode === 'dark' ? 'secondary' : 'primary'}
              startIcon={<ArrowBackIcon />}
            >
              Back to Sign In
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ForgotPassword;